import React, { useState, useEffect, Suspense, lazy } from 'react'
import Modal from 'react-modal'
import ExportQuest from '../modals/ExportQuest'
import ImportText from '../modals/ImportText'
import VideoRecorder from '../answers/VideoRecorder'
import ScreenRecorder from '../answers/ScreenRecorder'
import Reload from '../modals/Reload'
import NewStream from '../modals/NewStream'
import NewSpace from 'components/modals/NewSpace'
import UserImageUpdate from 'components/modals/UserImageUpdate'
import SpaceImageUpdate from 'components/modals/SpaceImageUpdate'
import StreamImageUpdate from 'components/modals/StreamImageUpdate'
import useStore from 'state/knovStore'
import HandCashError from '../modals/HandCashError'
import TwitterAuthError from '../modals/TwitterAuthError'
import TwitterMediaAlert from '../modals/TwitterMediaAlert'
import TwitterSuccess from '../modals/TwitterSuccess'
import styles from 'components/shared/modal.module.scss'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import Wallet from 'components/modals/Wallet'
import DeviceTransferModal from 'components/modals/DeviceTransferModal'
import { Wallet as WalletIcon, X, SmilePlus } from 'lucide-react'
import OrdinalsIcon from './OrdinalsIcon'
import Confirm from 'components/quests/Confirm'
import AuthModal from 'components/auth/AuthModal'
import EditProfile from 'components/users/EditProfile'
import OrdinalsViewer from 'components/modals/OrdinalsViewer'
import LeaderboardViewer from 'components/modals/LeaderboardViewer'
import TeamSettings from 'components/teams/TeamSettings'
import CreateStreamModal from 'components/modals/CreateStreamModal'
import NewAnswer from 'components/answers/NewAnswer'
import FollowListModal from 'components/users/FollowListModal'
import { SignalOptions } from 'components/votes/SignalOptions'
import FreeUpvalueIntro from 'components/modals/FreeUpvalueIntro'
import ReactionUpvalueIntro from 'components/modals/ReactionUpvalueIntro'
import DraftsSelector from 'components/modals/DraftsSelector'
import NewMusicModal from 'components/modals/NewMusicModal'
import useKeyboardHeight from 'hooks/useKeyboardHeight'
import { shouldCloseModalOnOverlayClick } from 'components/shared/modalBehavior'
import ReliableButton from './ReliableButton'
import {
    readNativeKeyboardState,
    NATIVE_KEYBOARD_CHANGE_EVENT,
} from 'components/PanelController/mobileKeyboardFocusBehavior'

// Lazy loaded components
const MetricsViewer = lazy(() => import('components/modals/MetricsViewer'))
const AdminConsole = lazy(() => import('components/admin/AdminConsole'))
const HostDealModal = lazy(() => import('components/modals/HostDealModal'))
const HostStreamPickerModal = lazy(() => import('components/modals/HostStreamPickerModal'))
// TDPViewer renders with three.js; keep it off the entry chunk.
const TDPViewer = lazy(() => import('components/modals/TDPViewer'))

const CUSTOM_MODAL_ICONS = {
    WalletIcon: <WalletIcon size={40} />,
    OrdinalsIcon: <OrdinalsIcon size={40} />,
    SmilePlus: <SmilePlus size={24} color="var(--color-text-secondary)" />,
}

const MOBILE_FULLSCREEN_MODAL_TYPES = new Set([
    'Auth',
    'Wallet',
    'DeviceTransfer',
    'OrdinalsViewer',
    'LeaderboardViewer',
    'TDPViewer',
    'MetricsViewer',
    'AdminConsole',
    'NewAnswer',
    'SignalOptions',
])

Modal.setAppElement('#knovigator-body')

// Setting top does not work bc react-modal uses inset property to control top margin.
const customStyles = {
    content: {
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, 0%)',
        width: '50dvw',
        maxWidth: '50dvw',
        maxHeight: '80dvh',
        padding: 0,
        borderRadius: '8px',
        background: 'var(--color-surface-panel)',
        border: '1px solid var(--color-border-default)',
        boxShadow: 'var(--color-shadow-overlay)',
    },
}

const mobileCustomStyles = {
    content: {
        width: '95dvw',
        maxWidth: '95dvw',
        maxHeight: '80dvh',
        padding: 0,
        background: 'var(--color-surface-panel)',
        border: '1px solid var(--color-border-default)',
    },
}

export default function KnovModal(props) {
    const isModalOpen = useStore(state => state.isModalOpen)
    const closeModal = useStore(state => state.actions.modalActions.closeModal)
    const modalParams = useStore(state => state.modalParams) || props.modalParams

    const logoSize = '36px'
    const hiddenModal = modalParams && modalParams.hidden

    const isMobileSurface =
        isMobile || (typeof window !== 'undefined' && Boolean(window.KNOVIGATOR_IS_MOBILE))
    const mobileStyles = isMobileSurface ? styles.mobileKnovModal : null
    const isMobileSafeAreaModal =
        isMobileSurface && MOBILE_FULLSCREEN_MODAL_TYPES.has(modalParams?.type)
    const mobileRemoveBtnStyles = isMobileSafeAreaModal ? styles.mobileRemoveBtn : null
    const requiresMobileFullscreenLayout =
        isMobileSurface &&
        (modalParams?.type === 'NewAnswer' || modalParams?.type === 'SignalOptions')
    const mobileFullscreenStyles =
        requiresMobileFullscreenLayout && modalParams?.type !== 'NewAnswer'
            ? styles.mobileFullscreenModal
            : null
    const requestedMobileCardTop =
        modalParams?.mobileCustomStyles?.top ?? modalParams?.customStyles?.top ?? '40px'
    const mobileCardTop =
        typeof requestedMobileCardTop === 'number'
            ? `${requestedMobileCardTop}px`
            : requestedMobileCardTop && requestedMobileCardTop !== 'auto'
            ? requestedMobileCardTop
            : '40px'

    const mobileComposer = isMobileSurface && modalParams?.type === 'NewAnswer'
    const [composerViewport, setComposerViewport] = useState(null)

    useEffect(() => {
        if (!mobileComposer || !isModalOpen) return
        const viewport = window.visualViewport
        const update = () =>
            setComposerViewport({
                height: viewport?.height || window.innerHeight,
                top: viewport?.offsetTop || 0,
                keyboardVisible:
                    readNativeKeyboardState(window)?.visible ??
                    window.innerHeight - (viewport?.height || window.innerHeight) > 100,
            })
        update()
        viewport?.addEventListener('resize', update)
        viewport?.addEventListener('scroll', update)
        window.addEventListener('resize', update)
        window.addEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, update)
        return () => {
            viewport?.removeEventListener('resize', update)
            viewport?.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
            window.removeEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, update)
        }
    }, [mobileComposer, isModalOpen])

    const mergedStyles = {
        overlay: {
            zIndex: 1600,
            backgroundColor: 'var(--color-overlay-bg)',
            backdropFilter: modalParams?.disableOverlayBlur ? 'none' : 'blur(4px)',
            WebkitBackdropFilter: modalParams?.disableOverlayBlur ? 'none' : 'blur(4px)',
        },
        content: {
            ...customStyles.content,
            ...(isMobileSurface && mobileCustomStyles.content),
            ...(modalParams?.customStyles ? modalParams.customStyles : {}),
            ...(isMobileSurface && modalParams?.mobileCustomStyles
                ? modalParams.mobileCustomStyles
                : {}),
            ...(requiresMobileFullscreenLayout && {
                position: 'fixed',
                inset: '0',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                margin: '0',
                width: '100dvw',
                maxWidth: '100dvw',
                minWidth: '0',
                height: '100dvh',
                maxHeight: '100dvh',
                transform: 'none',
                borderRadius: '0',
            }),
            // Special handling for NewAnswer modal to ensure proper scrolling
            ...(modalParams?.type === 'NewAnswer'
                ? {
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      maxHeight: '80vh',
                      ...(mobileComposer && {
                          position: 'fixed',
                          top: composerViewport?.top ?? 0,
                          bottom: 'auto',
                          height: composerViewport?.height ?? '100dvh',
                          maxHeight: composerViewport?.height ?? '100dvh',
                      }),
                  }
                : {}),
            // Card-style modals still need a safe viewport boundary. Apply this
            // after caller styles so a custom top/maxHeight cannot put their
            // controls under the status bar, home indicator, or Android nav.
            ...(isMobileSurface &&
                !isMobileSafeAreaModal && {
                    top: `max(${mobileCardTop}, calc(var(--modal-safe-area-top) + 12px))`,
                    maxWidth:
                        'calc(100dvw - var(--modal-safe-area-left) - var(--modal-safe-area-right) - 24px)',
                    maxHeight:
                        'calc(100dvh - var(--modal-safe-area-top) - var(--modal-safe-area-bottom) - 24px)',
                }),
        },
    }

    return hiddenModal ? (
        <div style={{ height: 0, width: 0 }}>{getModalContent(modalParams, closeModal)}</div>
    ) : (
        <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            shouldCloseOnOverlayClick={shouldCloseModalOnOverlayClick(modalParams)}
            style={mergedStyles}
            contentLabel="Knov Modal"
        >
            <div
                id="knov-modal"
                style={
                    mobileComposer && composerViewport?.keyboardVisible
                        ? { paddingBottom: 0 }
                        : undefined
                }
                className={cn(
                    'knov-modal-comp',
                    styles.knovModal,
                    mobileStyles,
                    isMobileSafeAreaModal && styles.mobileFullscreenSafeArea,
                    mobileFullscreenStyles,
                )}
            >
                {!modalParams?.noHeader && (
                    <div
                        className={cn(
                            styles.knovModalHeader,
                            modalParams?.type === 'Wallet' && styles.walletHeader,
                        )}
                    >
                        <div className={styles.knovModalBrand}>
                            {modalParams?.icon && (
                                <div className={styles.modalIcon}>
                                    {
                                        // use custom modal icon if specified, otherwise fallback to fa icon
                                        CUSTOM_MODAL_ICONS[modalParams?.icon] || (
                                            <div className={styles.fontIcon}>
                                                <i className={modalParams.icon} />
                                            </div>
                                        )
                                    }
                                </div>
                            )}
                            {modalParams?.title && (
                                <span className={styles.modalTitle}>{modalParams.title}</span>
                            )}
                        </div>
                    </div>
                )}
                {!modalParams?.hideCloseButton && (
                    <ReliableButton
                        type="button"
                        className={cn(styles.removeBtn, mobileRemoveBtnStyles)}
                        onClick={closeModal}
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </ReliableButton>
                )}
                <div
                    className={cn(
                        'knov-modal-content',
                        styles.knovModalContent,
                        modalParams?.type === 'NewAnswer' && styles.newAnswerModalContent,
                        modalParams?.type === 'OrdinalsViewer' && styles.ordinalsModalContent,
                    )}
                >
                    {getModalContent(modalParams, closeModal)}
                </div>
            </div>
        </Modal>
    )
}

// Wrapper component to handle focusing NewAnswer when modal opens
function NewAnswerWithFocus({ modalParams, closeModal }) {
    const newAnswerRef = React.useRef(null)
    const { isKeyboardVisible } = useKeyboardHeight()

    React.useEffect(() => {
        // Focus the editor after a short delay to ensure it's mounted
        const timer = setTimeout(() => {
            if (newAnswerRef.current && newAnswerRef.current.focus) {
                newAnswerRef.current.focus()
            }
        }, 100)

        return () => clearTimeout(timer)
    }, [])

    return (
        <div
            className={cn(
                styles.newAnswerModalWrapper,
                isKeyboardVisible && styles.keyboardVisible,
            )}
        >
            <NewAnswer {...modalParams} closeModal={closeModal} ref={newAnswerRef} />
        </div>
    )
}

function getModalContent(modalParams, closeModal) {
    if (!modalParams) return null

    switch (modalParams.type) {
        case 'ExportQuest':
            return <ExportQuest modalParams={modalParams} closeModal={closeModal} />
        case 'ImportText':
            return <ImportText modalParams={modalParams} closeModal={closeModal} />
        case 'VideoRecorder':
            return <VideoRecorder modalParams={modalParams} closeModal={closeModal} />
        case 'ScreenRecorder':
            return <ScreenRecorder modalParams={modalParams} closeModal={closeModal} />
        case 'Reload':
            return <Reload modalParams={modalParams} closeModal={closeModal} />
        case 'NewStream':
            return <NewStream modalParams={modalParams} closeModal={closeModal} />
        case 'NewSpace':
            return <NewSpace modalParams={modalParams} closeModal={closeModal} />
        case 'SpaceImageUpdate':
            return <SpaceImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'UserImageUpdate':
            return <UserImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'StreamImageUpdate':
            return <StreamImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'HandCashError':
            return <HandCashError modalParams={modalParams} closeModal={closeModal} />
        case 'TwitterAuthError':
            return <TwitterAuthError modalParams={modalParams} closeModal={closeModal} />
        case 'TwitterMediaAlert':
            return <TwitterMediaAlert modalParams={modalParams} closeModal={closeModal} />
        case 'TwitterSuccess':
            return <TwitterSuccess modalParams={modalParams} closeModal={closeModal} />
        case 'Message':
            return <div>{modalParams.message}</div>
        case 'Wallet': {
            return <Wallet modalParams={modalParams} closeModal={closeModal} />
        }
        case 'DeviceTransfer':
            return <DeviceTransferModal modalParams={modalParams} closeModal={closeModal} />
        case 'DraftsSelector':
            return <DraftsSelector modalParams={modalParams} closeModal={closeModal} />
        case 'OrdinalsViewer':
            return <OrdinalsViewer modalParams={modalParams} closeModal={closeModal} />
        case 'LeaderboardViewer':
            return <LeaderboardViewer modalParams={modalParams} closeModal={closeModal} />
        case 'TDPViewer':
            return (
                <Suspense fallback={null}>
                    <TDPViewer modalParams={modalParams} closeModal={closeModal} />
                </Suspense>
            )
        case 'MetricsViewer':
            return (
                <Suspense
                    fallback={
                        <div
                            className="loading-fallback"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '50vh',
                                flexDirection: 'column',
                            }}
                        >
                            <div style={{ marginBottom: '20px' }}>
                                <i className="fa fa-circle-o-notch fa-spin fa-2x" />
                            </div>
                            <div>Loading metrics...</div>
                        </div>
                    }
                >
                    <MetricsViewer modalParams={modalParams} closeModal={closeModal} />
                </Suspense>
            )
        case 'HostStreamPicker':
            return (
                <Suspense fallback={null}>
                    <HostStreamPickerModal modalParams={modalParams} closeModal={closeModal} />
                </Suspense>
            )
        case 'HostDeal':
            return (
                <Suspense fallback={null}>
                    <HostDealModal modalParams={modalParams} closeModal={closeModal} />
                </Suspense>
            )
        case 'AdminConsole':
            return (
                <Suspense
                    fallback={
                        <div
                            className="loading-fallback"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '50vh',
                            }}
                        >
                            <i className="fa fa-circle-o-notch fa-spin fa-2x" />
                        </div>
                    }
                >
                    <AdminConsole closeModal={closeModal} />
                </Suspense>
            )
        case 'Confirm':
            return <Confirm modalParams={modalParams} closeModal={closeModal} />
        case 'Auth':
            return <AuthModal modalParams={modalParams} closeModal={closeModal} />
        case 'EditProfile':
            return <EditProfile modalParams={modalParams} closeModal={closeModal} />
        case 'StreamSettings':
            return (
                <TeamSettings
                    team={modalParams.team}
                    teamId={modalParams.team.id}
                    panelId={modalParams.panelId}
                    filter={modalParams.filter}
                    closeModal={closeModal}
                />
            )
        case 'CreateStream':
            return (
                <CreateStreamModal
                    panelId={modalParams.panelId}
                    filter={modalParams.filter}
                    closeModal={closeModal}
                />
            )
        case 'NewAnswer':
            return <NewAnswerWithFocus modalParams={modalParams} closeModal={closeModal} />
        case 'FollowList':
            return <FollowListModal modalParams={modalParams} closeModal={closeModal} />
        case 'SignalOptions':
            return <SignalOptions {...modalParams} closeModal={closeModal} />
        case 'FreeUpvalueIntro':
            return <FreeUpvalueIntro closeModal={closeModal} />
        case 'ReactionUpvalueIntro':
            return <ReactionUpvalueIntro closeModal={closeModal} />
        case 'NewMusic':
            return <NewMusicModal {...modalParams} closeModal={closeModal} />
    }
}
